const HISTOGRAM_KEYS = [
  'perf_histogram_16_100', 'perf_histogram_100_200',
  'perf_histogram_200_500', 'perf_histogram_500_plus',
] as const;
type tBucket = typeof HISTOGRAM_KEYS[number];
type tSeries = 'web_vitals' | 'slow' | 'other_perf';
type tInteraction = { duration: number; bucket: tBucket };
type tCursor = { total: number | null; observed: number; histogram: Record<tBucket, number>; under100: number | null };
const recent = new Map<number, tInteraction>();
const cursors = new Map<tSeries, tCursor>();
const HISTORY_LIMIT = 2048;
let observed = 0;
const histogram = { perf_histogram_16_100: 0, perf_histogram_100_200: 0, perf_histogram_200_500: 0, perf_histogram_500_plus: 0 };

const bucketFor = (duration: number): tBucket => HISTOGRAM_KEYS[
  duration < 100 ? 0 : duration < 200 ? 1 : duration < 500 ? 2 : 3
];

export const recordPerfInteraction = (id: number, duration: number): void => {
  try {
    if (!Number.isFinite(id) || id <= 0 || !Number.isFinite(duration) || duration < 0) return;
    const previous = recent.get(id);
    if (previous && previous.duration >= duration) return;
    const bucket = bucketFor(duration);
    if (previous) histogram[previous.bucket] -= 1;
    else observed += 1;
    histogram[bucket] += 1;
    recent.set(id, { duration, bucket });
    if (recent.size > HISTORY_LIMIT) {
      const oldest = recent.keys().next().value;
      if (oldest !== undefined) recent.delete(oldest);
    }
  } catch {
    // Counters must never interrupt input handling or bootstrap telemetry.
  }
};

export const getPerfInteractionCount = (): number | null => {
  try {
    // Event Timing omits entries below durationThreshold. Only the native
    // document count includes those interactions; interaction IDs are not counts.
    const count = (performance as Performance & { interactionCount?: number }).interactionCount;
    return typeof count === 'number' && Number.isInteger(count) && count >= observed ? count : null;
  } catch { return null; }
};

export const takePerfCounters = (series: tSeries = 'web_vitals') => {
  const total = getPerfInteractionCount();
  const under100 = total === null ? null : total - histogram.perf_histogram_100_200 - histogram.perf_histogram_200_500 - histogram.perf_histogram_500_plus;
  const previous = cursors.get(series);
  // Each series owns a cumulative cursor. A longer entry received after a
  // report can move one interaction between bins, yielding signed corrections.
  const snapshot = {
    perf_counter_source: total === null ? 'observed_16ms' : 'native',
    perf_interactions_total: total,
    perf_interactions_since_report: total === null ? null : total - (previous?.total ?? 0),
    perf_observed_interactions_total: observed,
    perf_observed_interactions_since_report: observed - (previous?.observed ?? 0),
    perf_histogram_under_100: under100 === null ? null : under100 - (previous?.under100 ?? 0),
    perf_histogram_16_100: histogram.perf_histogram_16_100 - (previous?.histogram.perf_histogram_16_100 ?? 0),
    perf_histogram_100_200: histogram.perf_histogram_100_200 - (previous?.histogram.perf_histogram_100_200 ?? 0),
    perf_histogram_200_500: histogram.perf_histogram_200_500 - (previous?.histogram.perf_histogram_200_500 ?? 0),
    perf_histogram_500_plus: histogram.perf_histogram_500_plus - (previous?.histogram.perf_histogram_500_plus ?? 0),
  };
  // An unavailable read must not erase the last valid native baseline: when
  // it recovers, subtract that baseline instead of reporting the tab twice.
  cursors.set(series, { total: total ?? previous?.total ?? null, observed, under100: under100 ?? previous?.under100 ?? null, histogram: { ...histogram } });
  return snapshot;
};
